import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Factory, Users, Globe, Award } from 'lucide-react';
import SEO from '@/components/SEO';
import { useLang } from '@/i18n/LanguageContext';

const Home = () => {
  const { t } = useLang();
  const stats = [
    { label: 'Founded', value: '1960', icon: Factory },
    { label: 'Installations', value: '1000+', icon: CheckCircle },
    { label: 'Clients', value: 'Pan India', icon: Globe },
  ];

  const features = [
    {
      icon: '🔥',
      title: 'Customized Heat Treatment',
      description:
        'Every furnace is engineered around your specific metallurgy, throughput targets and floor plan — from single-chamber units to multi-zone conveyor lines.',
    },
    {
      icon: '🤝',
      title: 'Enduring Relationships',
      description:
        'Six decades of partnerships built on responsive service, in-house spares manufacturing and on-site commissioning teams across India.',
    },
    {
      icon: '⚙️',
      title: 'Process-Appropriate Design',
      description:
        'We select the right furnace type, atmosphere and quench medium to match your steel grade, case depth and hardness requirements.',
    },
    {
      icon: '👨‍🏭',
      title: 'Professional Technical Team',
      description:
        'Experienced metallurgists and engineers under one roof — ensuring repeatable thermal results that meet specification on day one.',
    },
  ];

  const products = [
    {
      id: 'ARKO-RRF',
      name: 'ARKO-RRF',
      category: 'Rotary Retort Furnace',
      temp: 'Up to 1200°C',
      capacity: '50-500 kg',
      image: '/rrf1.jpeg',
    },
    {
      id: 'ARKO-CMBF',
      name: 'ARKO-CMBF',
      category: 'Continuous Hardening',
      temp: 'Up to 1000°C',
      capacity: '50-500 kg',
      image: '/continous_mesh_belt_roller_type.jpeg',
    },
    {
      id: 'ARKO-BHF',
      name: 'ARKO-BHF',
      category: 'Bogie Hearth Furnace',
      temp: 'Up to 1200°C',
      capacity: '50 - 500 kg',
      image: '/bhf.jpeg',
    },
  ];

  return (
    <div className="animate-fadeIn">
      <SEO
        path="/"
        title="Arkotherm Furnaces — ISO 9001 Certified Industrial Furnace Manufacturer | Amritsar & Ludhiana"
        description="ISO 9001 Certified manufacturer of industrial furnaces and heat-treatment systems since 1960. Continuous mesh belt, rotary retort, gas carburizing, annealing and normalizing furnaces. Factories at Amritsar & Ludhiana."
      />
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'url(/continuous_mesh.jpeg)', backgroundSize: 'cover', backgroundPosition: 'center' }}></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500/20 border border-orange-500 rounded-full text-sm font-semibold mb-6">
                <Award size={16} className="text-yellow-400" />
                ISO 9001 CERTIFIED · INDUSTRIAL FURNACE MANUFACTURERS
              </div>
              <h1 className="text-4xl lg:text-6xl font-bold mb-6 leading-tight">
                Built to Heat —{' '}
                <span className="gradient-text">Built to Perform</span>
              </h1>
              <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                Six decades of designing, manufacturing and commissioning custom-engineered
                industrial furnaces and turnkey heat-treatment lines for India's most demanding
                manufacturers — built for durability, precision and production-scale repeatability.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/contact"
                  className="px-8 py-4 gradient-bg text-white font-semibold rounded-full hover:shadow-2xl transition-all duration-300 inline-flex items-center gap-2"
                  data-testid="hero-request-quote-btn"
                >
                  Request Quote <ArrowRight size={20} />
                </Link>
                <Link
                  to="/products"
                  className="px-8 py-4 bg-white/10 backdrop-blur-sm border border-white/20 text-white font-semibold rounded-full hover:bg-white/20 transition-all duration-300"
                  data-testid="hero-explore-products-btn"
                >
                  Explore Products
                </Link>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-6 mt-12">
                {stats.map((stat, index) => {
                  const Icon = stat.icon;
                  return (
                    <div key={index} className="text-center lg:text-left" data-testid={`stat-${stat.label.toLowerCase()}`}>
                      <div className="flex items-center justify-center lg:justify-start gap-2 mb-2">
                        <Icon className="text-yellow-400" size={20} />
                        <div className="text-2xl lg:text-3xl font-bold">{stat.value}</div>
                      </div>
                      <div className="text-sm text-gray-400">{stat.label}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Featured Product Card — Normalizing Furnace (user-uploaded image) */}
            <div className="hidden lg:block">
              <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6 shadow-2xl hover-lift">
                <img
                  src="/nf.jpeg"
                  alt="Arkotherm Normalizing Furnace"
                  className="w-full h-64 object-cover rounded-xl mb-4"
                  data-testid="hero-featured-image"
                />
                <h3 className="text-2xl font-bold mb-2">Normalizing Furnace — ARKO-NF</h3>
                <p className="text-gray-300 mb-4">Stainless-steel construction · Up to 1000°C · Forced-air recirculation</p>
                <Link
                  to="/products"
                  className="inline-flex items-center gap-2 text-yellow-400 hover:text-yellow-300 font-semibold"
                >
                  View product <ArrowRight size={16} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="https://images.unsplash.com/photo-1504328345606-18bbc8c9d7d1?w=600&h=500&fit=crop"
                alt="Industrial heat line"
                className="rounded-2xl shadow-2xl"
              />
            </div>
            <div>
              <h2 className="text-4xl font-bold mb-6 text-gray-900">
                About <span className="gradient-text">Arkotherm</span>
              </h2>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Arkotherm is an <span className="font-semibold">ISO 9001 Certified</span> manufacturer of
                industrial furnaces, heat-treatment systems and turnkey thermal lines, with manufacturing
                facilities at <span className="font-semibold">Amritsar and Ludhiana</span>. Since
                1960 we have combined metallurgical science with pragmatic engineering to deliver
                repeatable, production-grade thermal solutions to manufacturers across India.
              </p>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-orange-500 flex-shrink-0 mt-1" size={20} />
                  <div>
                    <span className="font-semibold text-gray-900">Custom lines</span>
                    <span className="text-gray-700"> — from single furnaces to multi-zone conveyor lines with integrated quench and temper.</span>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-orange-500 flex-shrink-0 mt-1" size={20} />
                  <div>
                    <span className="font-semibold text-gray-900">Controls &amp; automation</span>
                    <span className="text-gray-700"> — PLC/HMI, recipe management, alarm logging and safety interlocks built-in.</span>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-orange-500 flex-shrink-0 mt-1" size={20} />
                  <div>
                    <span className="font-semibold text-gray-900">Service &amp; spares</span>
                    <span className="text-gray-700"> — lifetime support, in-house spares manufacturing and trained service engineers.</span>
                  </div>
                </li>
              </ul>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/contact"
                  className="px-6 py-3 gradient-bg text-white font-semibold rounded-full hover:shadow-lg transition-all duration-300"
                  data-testid="about-request-concept-btn"
                >
                  Request a Concept
                </Link>
                <a
                  href="/catalog.pdf"
                  download="catalog.pdf"
                  className="px-6 py-3 bg-gray-100 text-gray-900 font-semibold rounded-full hover:bg-gray-200 transition-all duration-300"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Download Catalog
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="section-padding bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4 text-gray-900">
              Featured <span className="gradient-text">Products</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Annealing, Continuous Mesh Belt, Retort, Gas Carburizing and Spare Parts — explore our standard models or request a custom concept.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {products.map((product) => (
              <div
                key={product.id}
                className="bg-white rounded-xl overflow-hidden shadow-lg hover-lift"
                data-testid={`product-card-${product.id}`}
              >
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-48 object-cover"
                />
                <div className="p-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{product.name}</h3>
                  <p className="text-orange-600 font-semibold mb-3">{product.category}</p>
                  <div className="text-gray-600 space-y-1 mb-4">
                    <p>{product.temp}</p>
                    <p>{product.capacity}</p>
                  </div>
                  <Link
                    to="/products"
                    className="inline-flex items-center gap-2 text-orange-600 hover:text-orange-700 font-semibold"
                  >
                    View Details <ArrowRight size={16} />
                  </Link>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link
              to="/products"
              className="inline-flex items-center gap-2 px-8 py-4 gradient-bg text-white font-semibold rounded-full hover:shadow-xl transition-all duration-300"
              data-testid="view-all-products-btn"
            >
              View All Products <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="section-padding dark-gradient text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">
              Why Choose <span className="gradient-text">Arkotherm?</span>
            </h2>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto">
              Superior product quality, ethical practices, and precise engineering — trusted by industry leaders across India.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 hover-lift"
              >
                <div className="text-4xl mb-4">{feature.icon}</div>
                <h4 className="text-xl font-bold mb-3">{feature.title}</h4>
                <p className="text-gray-300 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-gradient-to-r from-orange-500 via-yellow-500 to-orange-500">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-4">
            Let's build heat together
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Tell us your capacity and target throughput — we'll deliver a concept within 48 hours.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/contact"
              className="px-8 py-4 bg-white text-orange-600 font-bold rounded-full hover:shadow-2xl transition-all duration-300"
              data-testid="cta-request-quote-btn"
            >
              Request Quote
            </Link>
            <a
              href="/catalog.pdf"
              download="catalog.pdf"
              className="px-8 py-4 bg-white/20 backdrop-blur-sm border-2 border-white text-white font-bold rounded-full hover:bg-white/30 transition-all duration-300"
            >
              Download Catalog
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
